import { useState, useEffect, useCallback, useContext, useRef } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import { Context as GeneralContext } from '@/context'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import WpContactForm7 from '@/components/WpContactForm7'
import Footer from '@/components/Footer'
import Meta from '@/components/Meta'
import actions from '@/actions'
import * as formActions from '@/actions/form'
import { getSlug } from '@/utils/path'
import style from './style'

const useStyles = createUseStyles(style)

const Contact = () => {
  const { setPageAnimationReady, headerHeight } = useContext(GeneralContext)
  const classes = useStyles({ headerHeight })
  const [load, setLoad] = useState(false)
  const formId = useRef()
  const location = useLocation()
  const stub = useRef(getSlug(location.pathname))

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, currentForm, strings } = useSelector((state) => ({
    current: state.page.current,
    page: state.page.pages[stub.current] || {},
    currentForm: state.form[formId.current] || [],
    strings: state.language.string,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const updatePageReady = useCallback((index) => dispatch(actions.loading.setReady(index)), [dispatch])
  const fetchForm = useCallback((id) => dispatch(formActions.fetchForm(id)), [dispatch])
  const sendContactForm = useCallback((id, data, isMultipart) => dispatch(formActions.sendContactForm(id, data, isMultipart)), [dispatch])
  const setSiteLoaded = useCallback(() => dispatch(actions.loading.setSiteLoaded()), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    updatePageReady(true)
    setSiteLoaded()
    setLoad(true)
  }
  /*------------------------------
  If Form does not exist call API form
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0) {
      formId.current = page.contact_form_id
      fetchForm(page.contact_form_id)
    }
  }, [page])

  /*------------------------------
  Complete Preload if form exist
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(currentForm).length > 0) completePreload()
  }, [currentForm])

  /*------------------------------
  Initialize
  ------------------------------*/
  const initialize = () => {
    if (current !== stub.current) setCurrentPage(stub.current, 'pages')
    getPage(stub.current)
  }

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Activate Page Animation
  ------------------------------*/
  useEffect(() => {
    if (load) setPageAnimationReady(true)
  }, [load])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta
        title={page.yoast_meta.find((y) => y.property?.includes('title')).content || page.title.rendered}
        meta={page.yoast_meta}
        schema={page.yoast_json_ld}
      />
    )
  }

  /*------------------------------
  Render Form
  ------------------------------*/
  const renderForm = () => {
    return (
      <div className={`${classes.formContent} ${classes.formContentCenter}`}>
        <div className={classes.pretitle}>{page.contact_form_title}</div>
        <WpContactForm7
          fields={currentForm}
          formID={strings.appointment_form_id}
          sendContactForm={sendContactForm}
          submitLabel={strings.send_label}
        />
      </div>
    )
  }

  const renderGrid = (children) => {
    return (
      <div className={classes.wrapper}>
        <div className={classes.left}>
          <div
            className={classes.address}
            dangerouslySetInnerHTML={{ __html: page.contact_address }}
          />
          <div className={classes.iconsList}>
            <div className={classes.itemList}>
              <svg><use xlinkHref="#ico-phone" /></svg>
              <p><a href={`tel:${page.contact_tel}`}>{page.contact_tel}</a></p>
            </div>
            <div className={classes.itemList}>
              <svg><use xlinkHref="#ico-mail" /></svg>
              <p><a href={`mailto:${page.contact_email}`}>{page.contact_email}</a></p>
            </div>
          </div>
        </div>
        <div className={classes.right}>
          {children}
        </div>
      </div>
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => <Footer />

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderGrid(renderForm())}
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageContact ${classes.root}`}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default Contact
